import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { User, LogOut, Menu, Home, BookOpen, Settings, LayoutDashboard, FlaskConical, MessageSquare, FileText, Target, Users, Rss, CircleUser, Gift, Trophy, ScrollText, Compass, GraduationCap , ClipboardList, Sparkles, MoreHorizontal, LifeBuoy } from "lucide-react";
import { openSupport } from "@/components/SupportWidget";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AdminPreviewSwitcher } from "@/components/AdminPreviewSwitcher";
import { examStageForGrade } from "@shared/territories";
import { useTerritory } from "@/contexts/TerritoryContext";
import logoImage from "@assets/EduFocal-Circle Only (1)_1759251009690.png";
import { NotificationBell } from "@/components/NotificationBell";
import { ThemeToggle } from "@/components/ThemeToggle";
import { getDisplayName } from "@/lib/userDisplay";
import { StaffBadge } from "@/components/StaffBadge";

export function TopNavigation() {
  const { user, logout, isAuthenticated, adminGradeOverride, setAdminGradeOverride } = useAuth();
  const [location, navigate] = useLocation();
  const { toast } = useToast();

  // Fetch unread message count
  const { data: unreadData } = useQuery<{ count: number }>({
    queryKey: ['/api/messages/unread-count'],
    enabled: isAuthenticated && !!user,
    refetchInterval: 30000,
  });
  const unreadCount = unreadData?.count ?? 0;

  if (!isAuthenticated || !user) {
    return null;
  }

  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: "Logged out successfully",
        description: "You have been logged out of your account."
      });
      navigate("/");
    } catch (error) {
      toast({
        title: "Logout failed",
        description: "There was an error logging out. Please try again.",
        variant: "destructive"
      });
    }
  };

  const isAdmin = user && 'role' in user && user.role === 'admin';
  // Course builder entry point. Creators and admins both land on /creator/courses.
  const isCreator = user && 'role' in user && (user.role === 'creator' || user.role === 'admin');

  const { territory } = useTerritory();
  const taskStage = examStageForGrade(user?.grade);
  // Lower secondary sits no external exam, so it gets neither the territory's primary exam name
  // nor "Paper 2". What Jamaican schools actually set at grades 7 to 9 is a term test, and the
  // item was hidden entirely before, which meant that content could never be reached.
  const taskLabel =
    taskStage === "PRIMARY" ? `${territory.examName} Tasks` :
    taskStage === "CSEC" || taskStage === "CAPE" ? "Paper 2" :
    taskStage === "LOWER_SECONDARY" ? "Term Tests" : "";

  // The bar had eight items and overflowed, so the last of them scrolled off screen and could not
  // be reached at all. Only what a student opens most days stays on the bar; the rest moves into a
  // "More" menu that is one click away and always fully visible.
  const primaryNavItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, disabled: false },
    { path: "/learning", label: "Subjects", icon: FlaskConical, disabled: false },
    { path: "/lessons", label: "Lessons", icon: Sparkles, disabled: false },
    // Performance Tasks are the same engine across all three exams but are called different
    // things, and lower secondary sits no exam at all. Hard-coding "PEP Tasks" put a primary-school
    // label in a CSEC student's navigation.
    ...(taskStage
      ? [{ path: "/performance-tasks", label: taskLabel, icon: ClipboardList, disabled: false }]
      : []),
    { path: "/messages", label: "Messages", icon: MessageSquare, disabled: false },
  ];

  const moreNavItems = [
    { path: "/study-plan", label: "Study Plan", icon: Target, disabled: false },
    { path: "/progress-report", label: "Progress Report", icon: FileText, disabled: false },
    // The Beyond School interest courses are written for primary learners and are now bounded to
    // grades 4 to 6 in the data, so the catalogue is empty for anyone else. Do not offer the page.
    ...(taskStage === "PRIMARY"
      ? [{ path: "/courses", label: "Courses", icon: Compass, disabled: false }]
      : []),
  ];

  // The hamburger keeps showing everything, so nothing becomes unreachable.
  const navItems = [...primaryNavItems, ...moreNavItems];

  // Only shown in hamburger menu, not desktop nav bar
  const mobileOnlyItems = [
    { path: "/practice-papers", label: "Practice Papers", icon: ScrollText },
    { path: "/leaderboard", label: "Leaderboards", icon: Trophy },
    { path: "/claim-prize", label: "Prizes", icon: Gift },
  ];

  const socialMenuItems = [
    { path: "/social", label: "Social", icon: Users },
    { path: "/feed", label: "Activity Feed", icon: Rss },
  ];

  return (
    <div className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container max-w-6xl mx-auto">
        <div className="flex h-14 items-center gap-6 px-4">
          <div className="flex items-center">
            <button 
              onClick={() => navigate("/dashboard")}
              className="relative hover-elevate active-elevate-2 rounded-md p-1"
              data-testid="button-logo-home"
            >
              <img 
                src={logoImage} 
                alt="EduFocal Logo" 
                className="h-10 w-auto"
                data-testid="img-logo"
              />
            </button>
          </div>
          
          <nav className="hidden md:flex items-center gap-0.5 flex-1 min-w-0">
            {primaryNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location === item.path;
              const isMessages = item.path === "/messages";
              const showBadge = isMessages && unreadCount > 0;
              
              return (
                <Button
                  key={item.path}
                  variant="ghost"
                  onClick={() => !item.disabled && navigate(item.path)}
                  disabled={item.disabled}
                  className={`relative flex items-center gap-1.5 px-2.5 shrink-0 ${isActive ? 'bg-accent' : ''} ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-sm">{item.label}</span>
                  {item.disabled && <span className="text-xs text-muted-foreground ml-1">(Coming Soon)</span>}
                  {showBadge && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-1 -right-1 h-5 min-w-[20px] flex items-center justify-center p-0 text-xs"
                      data-testid="badge-unread-count"
                    >
                      {unreadCount}
                    </Badge>
                  )}
                </Button>
              );
            })}

            {/* Everything else, one click away and never scrolled off the edge. */}
            {moreNavItems.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className={`flex items-center gap-1.5 px-2.5 shrink-0 ${moreNavItems.some(i => i.path === location) ? "bg-accent" : ""}`}
                    data-testid="nav-more"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                    <span className="text-sm">More</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-52">
                  {moreNavItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <DropdownMenuItem
                        key={item.path}
                        onSelect={() => navigate(item.path)}
                        className="gap-2"
                        data-testid={`nav-more-${item.label.toLowerCase().replace(/ /g, "-")}`}
                      >
                        <Icon className="h-4 w-4" />
                        {item.label}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </nav>
          
          <div className="flex items-center space-x-2 ml-auto">
          <AdminPreviewSwitcher />
          <ThemeToggle />
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="icon" 
                className="h-10 w-10 md:h-9 md:w-9 border-2 md:border-0 md:hover-elevate" 
                data-testid="button-menu"
              >
                <Menu className="h-5 w-5 md:h-4 md:w-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none flex items-center gap-1">{user.firstName} {user.lastName}{user.role === 'admin' && <StaffBadge />}</p>
                  <p className="text-xs leading-none text-muted-foreground">{getDisplayName(user)}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              
              {navItems.map((item) => {
                const Icon = item.icon;
                const isMessages = item.path === "/messages";
                const showBadge = isMessages && unreadCount > 0;
                return (
                  <DropdownMenuItem 
                    key={item.path}
                    onClick={() => !item.disabled && navigate(item.path)} 
                    disabled={item.disabled}
                    data-testid={`link-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    <span>{item.label}</span>
                    {showBadge && (
                      <Badge variant="destructive" className="ml-2">
                        {unreadCount}
                      </Badge>
                    )}
                    {item.disabled && <span className="ml-auto text-xs text-muted-foreground">(Soon)</span>}
                  </DropdownMenuItem>
                );
              })}
              {mobileOnlyItems.map((item) => {
                const Icon = item.icon;
                return (
                  <DropdownMenuItem
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    data-testid={`link-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    <span>{item.label}</span>
                  </DropdownMenuItem>
                );
              })}

              <DropdownMenuSeparator />
              {socialMenuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <DropdownMenuItem
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    data-testid={`link-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    <span>{item.label}</span>
                  </DropdownMenuItem>
                );
              })}

              {isCreator && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/creator/courses")} data-testid="link-creator-courses">
                    <GraduationCap className="mr-2 h-4 w-4" />
                    <span>My Courses</span>
                  </DropdownMenuItem>
                </>
              )}

              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="link-admin">
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Admin Dashboard</span>
                  </DropdownMenuItem>
                </>
              )}
              
              <DropdownMenuSeparator />
              
              <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="link-profile">
                <CircleUser className="mr-2 h-4 w-4" />
                <span>My Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="link-settings">
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </DropdownMenuItem>
              {/* After the menu has closed, or Radix hands focus back to the trigger and out of the form. */}
              <DropdownMenuItem onClick={() => setTimeout(() => openSupport(() => navigate("/contact")), 150)} data-testid="link-support">
                <LifeBuoy className="mr-2 h-4 w-4" />
                <span>Support</span>
              </DropdownMenuItem>
              
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="button-logout">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        </div>
      </div>
    </div>
  );
}